import { Icon, Portal } from '@chakra-ui/react';
import {
  ProductQuantityDisplayTypeEnum,
  ProductResWithMandatory,
  ProductTypeEnum,
} from '@monetize/types/app';
import { FC } from 'react';
import { MdClose } from 'react-icons/md';
import {
  MButton,
  MCard,
  MCheckbox,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MTable,
  MTbody,
  MTd,
  MText,
  MTh,
  MThead,
  MTr,
} from '../../../../components/Monetize';
import HeaderCellText from './HeaderCellText';

const PRODUCT_TYPES_WITHOUT_TIER_LOCK = new Set([
  ProductTypeEnum.MIN_COMMIT,
  ProductTypeEnum.ONETIME,
  ProductTypeEnum.DISCOUNT,
  ProductTypeEnum.ONETIME_PREPAID_CREDIT,
  ProductTypeEnum.ONETIME_TOKEN,
  ProductTypeEnum.USAGE,
]);

interface ProductAdvancedSettingsDrawerProps {
  allowQuantityDropdown: boolean;
  enablePriceTierLockEligible: boolean;
  products: ProductResWithMandatory[];
  isReadOnly: boolean;
  isOpen: boolean;
  onClose: () => void;
  onUpdateProducts: (updatedProducts: ProductResWithMandatory[]) => void;
}

const ProductAdvancedSettingsDrawer: FC<ProductAdvancedSettingsDrawerProps> = ({
  allowQuantityDropdown,
  enablePriceTierLockEligible,
  products,
  isReadOnly,
  isOpen,
  onClose,
  onUpdateProducts,
}) => {
  const tierLockEligibleIndices = products.reduce<number[]>(
    (acc, product, index) => {
      if (!PRODUCT_TYPES_WITHOUT_TIER_LOCK.has(product.productType)) {
        acc.push(index);
      }
      return acc;
    },
    [],
  );

  const allQtyDropdown =
    products.length > 0 &&
    products.every(
      ({ quantityDisplayType }) =>
        quantityDisplayType === ProductQuantityDisplayTypeEnum.DROPDOWN,
    );
  const someQtyDropdown = products.some(
    ({ quantityDisplayType }) =>
      quantityDisplayType === ProductQuantityDisplayTypeEnum.DROPDOWN,
  );

  const allTierLock =
    tierLockEligibleIndices.length > 0 &&
    tierLockEligibleIndices.every(
      (index) => products[index]?.isPriceTierLockEligible,
    );
  const someTierLock = tierLockEligibleIndices.some(
    (index) => products[index]?.isPriceTierLockEligible,
  );

  return (
    <MDrawer
      open={isOpen}
      placement="end"
      size="lg"
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
    >
      <Portal>
        <MDrawerOverlay />
        <MDrawerPositioner>
          <MDrawerContent>
            <MDrawerCloseButton>
              <Icon asChild>
                <MdClose />
              </Icon>
            </MDrawerCloseButton>
            <MDrawerHeader borderBottomWidth="1px">
              Advanced Product Settings
            </MDrawerHeader>

            {/* Scrollable product rows with sticky header */}
            <MDrawerBody
              p={2}
              overflowY="auto"
              backgroundColor="tGray.drawerSidebar"
            >
              <MCard variant="borderless" mb="2">
                <MTable size="sm">
                  <MThead position="sticky" top={0} zIndex={1} bg="white">
                    <MTr _hover={{ bg: 'transparent' }}>
                      <MTh px={6} py={4}>
                        <HeaderCellText textProps={{ fontSize: 'sm' }}>
                          Products
                        </HeaderCellText>
                      </MTh>

                      {allowQuantityDropdown && (
                        <MTh px={4} py={4} textAlign="center">
                          <HeaderCellText
                            textProps={{ fontSize: 'sm', textAlign: 'center' }}
                            justifyContent="center"
                            tooltipLabel="Checked items will show a dropdown on quote quantity field with the available tiers."
                          >
                            Show Quantity as Dropdown
                          </HeaderCellText>
                          <MCheckbox
                            mt={2}
                            isDisabled={isReadOnly}
                            isChecked={allQtyDropdown}
                            isIndeterminate={!allQtyDropdown && someQtyDropdown}
                            onChange={(e) =>
                              onUpdateProducts(
                                products.map((p) => ({
                                  ...p,
                                  quantityDisplayType: e.target.checked
                                    ? ProductQuantityDisplayTypeEnum.DROPDOWN
                                    : ProductQuantityDisplayTypeEnum.INPUT,
                                })),
                              )
                            }
                          >
                            <MText
                              fontSize="sm"
                              fontWeight="semibold"
                              color="tPurple.base"
                            >
                              Select All
                            </MText>
                          </MCheckbox>
                        </MTh>
                      )}

                      {enablePriceTierLockEligible && (
                        <MTh px={4} py={4} textAlign="center">
                          <HeaderCellText
                            textProps={{ fontSize: 'sm', textAlign: 'center' }}
                            justifyContent="center"
                            tooltipLabel="Allows reps to optionally lock customers into a negotiated tiered price for this product during quoting."
                          >
                            Price Tier Lock Eligible
                          </HeaderCellText>
                          <MCheckbox
                            mt={2}
                            isDisabled={
                              isReadOnly || tierLockEligibleIndices.length === 0
                            }
                            isChecked={allTierLock}
                            isIndeterminate={!allTierLock && someTierLock}
                            onChange={(e) =>
                              onUpdateProducts(
                                products.map((p, index) => ({
                                  ...p,
                                  isPriceTierLockEligible:
                                    tierLockEligibleIndices.includes(index)
                                      ? e.target.checked
                                      : (p.isPriceTierLockEligible ?? false),
                                })),
                              )
                            }
                          >
                            <MText
                              fontSize="sm"
                              fontWeight="semibold"
                              color="tPurple.base"
                            >
                              Select All
                            </MText>
                          </MCheckbox>
                        </MTh>
                      )}
                    </MTr>
                  </MThead>

                  <MTbody>
                    {products.map((product, index) => (
                      <MTr key={product.id} _hover={{ bg: 'transparent' }}>
                        <MTd px={6} py={2}>
                          <MText color="tPurple.base" lineClamp={2}>
                            {product.name}
                          </MText>
                        </MTd>

                        {allowQuantityDropdown && (
                          <MTd px={4} py={2} textAlign="center">
                            <MCheckbox
                              isDisabled={isReadOnly}
                              isChecked={
                                product.quantityDisplayType ===
                                ProductQuantityDisplayTypeEnum.DROPDOWN
                              }
                              onChange={() =>
                                onUpdateProducts(
                                  products.map((p, i) =>
                                    i === index
                                      ? {
                                          ...p,
                                          quantityDisplayType:
                                            p.quantityDisplayType ===
                                            ProductQuantityDisplayTypeEnum.DROPDOWN
                                              ? ProductQuantityDisplayTypeEnum.INPUT
                                              : ProductQuantityDisplayTypeEnum.DROPDOWN,
                                        }
                                      : p,
                                  ),
                                )
                              }
                            />
                          </MTd>
                        )}

                        {enablePriceTierLockEligible && (
                          <MTd px={4} py={2} textAlign="center">
                            {!PRODUCT_TYPES_WITHOUT_TIER_LOCK.has(
                              product.productType,
                            ) && (
                              <MCheckbox
                                isDisabled={isReadOnly}
                                isChecked={
                                  product.isPriceTierLockEligible ?? false
                                }
                                onChange={() =>
                                  onUpdateProducts(
                                    products.map((p, i) =>
                                      i === index
                                        ? {
                                            ...p,
                                            isPriceTierLockEligible:
                                              !p.isPriceTierLockEligible,
                                          }
                                        : p,
                                    ),
                                  )
                                }
                              />
                            )}
                          </MTd>
                        )}
                      </MTr>
                    ))}
                  </MTbody>
                </MTable>
              </MCard>
            </MDrawerBody>

            <MDrawerFooter justifyContent="center" borderTopWidth="1px">
              <MButton variant="cancel" onClick={onClose}>
                Close
              </MButton>
            </MDrawerFooter>
          </MDrawerContent>
        </MDrawerPositioner>
      </Portal>
    </MDrawer>
  );
};

export default ProductAdvancedSettingsDrawer;
